import { HttpErrorResponse, HttpEvent, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { AuthenticationService } from 'brain-data/service/auth/authentication.service';
import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { getUnenrichedSessionUrl } from 'shared/services/utils/url/url.service';
import { hasPathSegment, isSessionAuthorityUrl } from './protected-api-url';

/**
 * BRN-2208: the machine-readable discriminator in the backends' fail-closed 401 body
 * (`{"error": "unenriched_token", "missing_claim": "...", "message": "..."}`) -- brain's
 * `UnenrichedTokenResponse.kt` and brain-estream's mirror emit it byte-identically.
 */
const UNENRICHED_TOKEN_ERROR = 'unenriched_token';

/**
 * BRN-2148: the one brain route whose 401 reports a THIRD-PARTY credential failure (an ENERGY STAR
 * account's own credentials) through brain's API. It is inside the session-authority scope, so it
 * is carved out of the logout arm explicitly rather than by origin.
 */
const ENERGY_STAR_ACCOUNTS_ROUTE = 'energyStarAccounts';

@Injectable()
export class HttpErrorInterceptor implements HttpInterceptor {
  public constructor(
    private authenticationService: AuthenticationService,
    private router: Router,
  ) {}

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    return next.handle(request).pipe(
      catchError(err => {
        this.handleAuthenticationFailure(request, err);

        return throwError(() => err);
      }),
    );
  }

  private handleAuthenticationFailure(request: HttpRequest<any>, err: unknown): void {
    // BRN-2148: key off the credential THIS request actually carried -- `BearerInterceptor` is
    // registered first, so it has already attached the header by the time this interceptor sees
    // the request -- rather than off whatever happens to be in storage when the failure lands.
    // A 401 for a request that went out unauthenticated says nothing about the current session,
    // and when such a 401 arrived just after a sign-in had written the token, the previous
    // "is there a token now" test saw the brand-new token and logged the session straight out.
    if (!request.headers.has('authorization')) {
      return;
    }

    if (!(err instanceof HttpErrorResponse) || err.status !== 401) {
      return;
    }

    // BRN-2325 (design L2-2, L3 S6): the bearer now travels to an origin brain does not control --
    // the payments API (AC12) -- so "carried the credential" no longer implies "speaks for the
    // session". Only a 401 from the SESSION AUTHORITY scope (brain's own API, brain-iam) may change
    // session state at all. A payments 401 -- cold start, audience mismatch, clock skew, a token
    // expiring mid-load -- reaches neither branch below, is rethrown to its caller, and the page
    // renders its own sign-in state (AC11). This gate sits ahead of BOTH branches on purpose: L3
    // measured that the logout arm consulted no url predicate, so splitting the predicate without
    // gating here would have left every bearer-carrying payments 401 logging the customer out.
    // The closed set is: non-authority origin => no logout, no redirect, whatever the body.
    if (!isSessionAuthorityUrl(request.url)) {
      return;
    }

    // BRN-2208: an unenriched session is NOT a credential failure -- the token is valid, signed and
    // unexpired; the account simply is not provisioned for BRAIN. Logging it out would send the user
    // to sign-in, where the IdP re-issues the SAME unenriched token: the silent re-login loop. Show
    // the explicit state instead, and keep the session so the user can act on it.
    if (this.isUnenrichedSessionRejection(err)) {
      void this.router.navigate([getUnenrichedSessionUrl()]);

      return;
    }

    // A path SEGMENT, by this file's own S17 rule -- a url that merely mentions the word in a query
    // value is not the ENERGY STAR route (L6 cr-A5).
    if (!hasPathSegment(request.url, ENERGY_STAR_ACCOUNTS_ROUTE)) {
      this.authenticationService.logOut();
    }
  }

  /**
   * L3 SEC-2 -- the second of two independent conditions, both still required. The first -- the
   * response came from an origin that speaks for the session -- is enforced once, for every branch,
   * at the head of `handleAuthenticationFailure`, so no third-party origin can suppress a logout OR
   * keep a dead session alive by echoing this body back. This one is the body: a structured object
   * whose `error` field EXACTLY equals the contract code -- never a substring/`includes` test, never
   * a regex, never the human-readable `message` prose, which would let unrelated text (or a
   * near-miss code) keep a genuinely dead session alive.
   *
   * Anything that fails this condition -- expired, revoked, malformed or otherwise invalid
   * credentials -- falls through to the unchanged hard-logout path.
   */
  private isUnenrichedSessionRejection(err: HttpErrorResponse): boolean {
    if (typeof err.error !== 'object' || err.error === null) {
      return false;
    }

    return (err.error as { error?: unknown }).error === UNENRICHED_TOKEN_ERROR;
  }
}
